import { WizardPanel } from '@/shared/components/Wizard';
import { Button, IconButton, RadioButtonCard, RadioButtonCardProps, TimePicker } from '@schuettflix/react-components';
import { z } from 'zod';
import { useZodForm } from '@/shared/hooks/useZodForm';
import { Controller, ControllerProps, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import React, { useMemo, useState } from 'react';
import { BusinessCase } from '@schuettflix/interfaces';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, ChevronUpIcon } from '@schuettflix/icons-react';
import { LocationType, LocationTypeSchema, OpeningTime } from '../location/domain/LocationSchema';
import { LocationConfiguration } from '../location/domain/LocationConfigurationSchema.ts';
import { formatAddress } from '@/modules/location/domain/AddressSchema.ts';
import { getLimitedDateRanges } from '@/shared/utils/getLimitedDateRanges.ts';
import { DatePickerFieldInput } from '@/shared/inputs/DatePickerFieldInput.tsx';

const DispositionBaseSchema = z.object({
    dispositionDateRange: z.object({
        start: z.string().date(),
        end: z.string().date(),
    }),
    relatedLocationType: LocationTypeSchema,
});

export const DispositionFormDataSchema = z.discriminatedUnion('dispositionTimeframe', [
    z.object({
        ...DispositionBaseSchema.shape,
        dispositionTimeframe: z.enum(['MORNING', 'AFTERNOON', 'DURING_DAY']),
    }),
    z.object({
        ...DispositionBaseSchema.shape,
        dispositionTimeframe: z.enum(['CUSTOM']),
        customDispositionStartTime: z.string(),
        customDispositionEndTime: z.string(),
    }),
]);

export type DispositionFormData = z.infer<typeof DispositionFormDataSchema>;

export type DispositionConfigurationPanelProps = {
    defaultValues?: DispositionFormData;
    onSubmit: (data: DispositionFormData) => void;
    onClose: () => void;
    businessCase: BusinessCase;
    loadingLocationData?: LocationConfiguration;
    unloadingLocationData?: LocationConfiguration;
};

export const timeframeLabelLocaleKeys = {
    DURING_DAY: 'dispositionConfigurationPanel.dispositionTimeframe.option.duringDay.label',
    MORNING: 'dispositionConfigurationPanel.dispositionTimeframe.option.morning.label',
    AFTERNOON: 'dispositionConfigurationPanel.dispositionTimeframe.option.afternoon.label',
    CUSTOM: 'dispositionConfigurationPanel.dispositionTimeframe.option.custom.label',
} as const;

export const DispositionConfigurationPanel: React.FC<DispositionConfigurationPanelProps> = ({
    defaultValues,
    onSubmit,
    onClose,
    businessCase,
    loadingLocationData,
    unloadingLocationData,
}) => {
    const requiresManualRelatedLocationTypeSelection = businessCase.productType === 'TRANSPORT';
    const { t } = useTranslation();
    const methods = useZodForm({
        schema: DispositionFormDataSchema,
        defaultValues: {
            ...(requiresManualRelatedLocationTypeSelection
                ? {}
                : { relatedLocationType: businessCase.productType.includes('WASTE') ? 'LOADING' : 'UNLOADING' }),
            ...defaultValues,
        },
    });
    const { handleSubmit } = methods;
    const { minDate, maxDate } = getLimitedDateRanges();

    const { relatedLocationType, dispositionTimeframe } = useWatch(methods);
    const relatedLocation =
        relatedLocationType === 'LOADING'
            ? loadingLocationData
            : relatedLocationType === 'UNLOADING'
              ? unloadingLocationData
              : undefined;

    const hasSelectedRelatedLocationType = !!relatedLocationType;

    return (
        <WizardPanel
            noMaximize
            onClose={onClose}
            title={t('dispositionConfigurationPanel.title')}
            footer={<DispositionConfigurationPanelFooter onSubmit={handleSubmit(onSubmit)} />}
        >
            <FormProvider {...methods}>
                <form className="@container/material-product grid gap-8" onSubmit={handleSubmit(onSubmit)}>
                    <fieldset className="@xl:grid-cols-2 grid gap-4">
                        <legend className="font-numbers-md-strong col-span-full mb-2 block">
                            {t('dispositionConfigurationPanel.dispositionDateRange.legend')}
                        </legend>
                        <DatePickerFieldInput
                            name="dispositionDateRange"
                            rangeFields={{ from: 'start', to: 'end' }}
                            minDate={minDate.toISOString()}
                            maxDate={maxDate.toISOString()}
                            dataTest="delivery-daterange-input"
                            size="lg"
                            mode="range"
                            placeHolder={t('dispositionConfigurationPanel.dispositionDateRange.label')}
                        />
                    </fieldset>
                    {requiresManualRelatedLocationTypeSelection && (
                        <fieldset className="@xl:grid-cols-2 grid gap-4">
                            <legend className="font-numbers-md-strong col-span-full mb-2 block">
                                {t('dispositionConfigurationPanel.relatedLocationType.legend')}
                            </legend>
                            <LocationTypeInput
                                label={t('dispositionConfigurationPanel.relatedLocationType.loading.label')}
                                inputValue="LOADING"
                                description={
                                    loadingLocationData
                                        ? formatAddress(loadingLocationData.location.geoAddress.address)
                                        : undefined
                                }
                            />
                            <LocationTypeInput
                                label={t('dispositionConfigurationPanel.relatedLocationType.unloading.label')}
                                inputValue="UNLOADING"
                                description={
                                    unloadingLocationData
                                        ? formatAddress(unloadingLocationData.location.geoAddress.address)
                                        : undefined
                                }
                            />
                        </fieldset>
                    )}
                    {hasSelectedRelatedLocationType && (
                        <fieldset className="grid">
                            <legend className="font-numbers-md-strong col-span-full mb-2 block">
                                {t('dispositionConfigurationPanel.dispositionTimeframe.legend')}
                            </legend>
                            {relatedLocation && (
                                <LocationOpeningTimes
                                    locationType={relatedLocationType}
                                    openingTimes={relatedLocation?.location?.openingTimes ?? []}
                                />
                            )}
                            <div className="@xl:grid-cols-2 grid gap-4">
                                <DispositionTimeframeInput
                                    label={t(timeframeLabelLocaleKeys.DURING_DAY)}
                                    description={t(
                                        'dispositionConfigurationPanel.dispositionTimeframe.option.duringDay.description'
                                    )}
                                    inputValue="DURING_DAY"
                                />
                                <DispositionTimeframeInput
                                    label={t(timeframeLabelLocaleKeys.MORNING)}
                                    description={t(
                                        'dispositionConfigurationPanel.dispositionTimeframe.option.morning.description'
                                    )}
                                    inputValue="MORNING"
                                />
                                <DispositionTimeframeInput
                                    label={t(timeframeLabelLocaleKeys.AFTERNOON)}
                                    description={t(
                                        'dispositionConfigurationPanel.dispositionTimeframe.option.afternoon.description'
                                    )}
                                    inputValue="AFTERNOON"
                                />
                                <div className="flex flex-col gap-2">
                                    <DispositionTimeframeInput
                                        label={t(timeframeLabelLocaleKeys.CUSTOM)}
                                        description="&nbsp;"
                                        inputValue="CUSTOM"
                                    />
                                    {dispositionTimeframe === 'CUSTOM' && (
                                        <div className="flex items-center gap-4">
                                            <div className="grid flex-1">
                                                <CustomDispositionTimeInput
                                                    placeholder={t('location.openingTimes.placeholderTimeFrom')}
                                                    initialTimeToFocus="05:00"
                                                    name="customDispositionStartTime"
                                                />
                                            </div>
                                            <div className="flex-none">-</div>
                                            <div className="grid flex-1">
                                                <CustomDispositionTimeInput
                                                    placeholder={t('location.openingTimes.placeholderTimeTo')}
                                                    initialTimeToFocus="16:00"
                                                    name="customDispositionEndTime"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </fieldset>
                    )}
                </form>
            </FormProvider>
        </WizardPanel>
    );
};

interface DispositionConfigurationPanelFooterProps {
    onSubmit: () => void;
}

export const DispositionConfigurationPanelFooter: React.FC<DispositionConfigurationPanelFooterProps> = ({
    onSubmit,
}) => {
    const { t } = useTranslation();
    return (
        <div className="flex justify-end">
            <Button
                label={t('common.save')}
                size="sm"
                onClick={onSubmit}
                type="button"
                data-test="dispatching-configuration-proceed-button"
            />
        </div>
    );
};

type LocationTypeInputProps = Partial<Pick<ControllerProps, 'defaultValue'>> &
    Pick<RadioButtonCardProps, 'label' | 'inputValue' | 'description'>;

const LocationTypeInput: React.FC<LocationTypeInputProps> = React.forwardRef<HTMLInputElement, LocationTypeInputProps>(
    ({ defaultValue, label, inputValue, description }, _ref) => {
        const { control } = useFormContext<z.infer<typeof DispositionFormDataSchema>>();

        return (
            <Controller
                control={control}
                defaultValue={defaultValue ?? null}
                name="relatedLocationType"
                render={({ field: { ref: _, value, onChange, ...field } }) => {
                    return (
                        <RadioButtonCard
                            value={value}
                            inputValue={inputValue}
                            description={<span className="block truncate">{description}</span>}
                            label={label}
                            onChange={() => onChange(inputValue)}
                            {...field}
                        />
                    );
                }}
            />
        );
    }
);

type LocationOpeningTimesProps = {
    locationType: LocationType;
    openingTimes: OpeningTime[];
};

const weekdayMap = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5,
    sunday: 6,
} as const;
const weekdayLocaleKeys = [
    'dispositionConfigurationPanel.openingTimes.weekdays.monday',
    'dispositionConfigurationPanel.openingTimes.weekdays.tuesday',
    'dispositionConfigurationPanel.openingTimes.weekdays.wednesday',
    'dispositionConfigurationPanel.openingTimes.weekdays.thursday',
    'dispositionConfigurationPanel.openingTimes.weekdays.friday',
    'dispositionConfigurationPanel.openingTimes.weekdays.saturday',
    'dispositionConfigurationPanel.openingTimes.weekdays.sunday',
] as const;

function LocationOpeningTimes({ locationType, openingTimes }: LocationOpeningTimesProps) {
    const [isOpen, setIsOpen] = useState(true);
    const { t } = useTranslation();

    const enhancedOpeningTimes = useMemo(() => {
        return openingTimes.reduce<
            ({
                start: string;
                end: string;
            } | null)[]
        >(
            (acc, { weekday, start, end }) => {
                const weekdayIndex = weekdayMap[weekday];
                acc[weekdayIndex] = start && end ? { start, end } : null;

                return acc;
            },
            new Array<{
                start: string;
                end: string;
            } | null>(7).fill(null)
        );
    }, [openingTimes]);

    return (
        <aside className="bg-hovered rounded border p-4">
            <header className="flex items-center justify-between">
                <h4 className="font-copy-sm-strong">
                    {locationType === 'LOADING'
                        ? t('dispositionConfigurationPanel.openingTimes.title.loading')
                        : t('dispositionConfigurationPanel.openingTimes.title.unloading')}
                </h4>
                <IconButton
                    icon={isOpen ? ChevronUpIcon : ChevronDownIcon}
                    size="sm"
                    type="button"
                    onClick={() => setIsOpen(current => !current)}
                />
            </header>
            {isOpen && (
                <table className="font-copy-sm mt-2">
                    <tbody>
                        {enhancedOpeningTimes.map((time, index) => {
                            const weekdayKey = weekdayLocaleKeys[index];
                            return (
                                <tr key={index} className="pb-1 last:pb-0">
                                    <td className="pr-4">{t(weekdayKey)}</td>
                                    {time ? (
                                        <td>{t('forms.inputs.dispositionInput.timeframeTime', time)}</td>
                                    ) : (
                                        <td>{t('dispositionConfigurationPanel.openingTimes.closed')}</td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </aside>
    );
}

type DispositionTimeframeInputProps = Partial<Pick<ControllerProps, 'defaultValue'>> &
    Pick<RadioButtonCardProps, 'label' | 'description'> & {
        inputValue: DispositionFormData['dispositionTimeframe'];
    };

const DispositionTimeframeInput: React.FC<DispositionTimeframeInputProps> = React.forwardRef<
    HTMLInputElement,
    DispositionTimeframeInputProps
>(({ defaultValue, label, inputValue, description }, _ref) => {
    const { control } = useFormContext<z.infer<typeof DispositionFormDataSchema>>();

    return (
        <Controller
            control={control}
            defaultValue={defaultValue ?? null}
            name="dispositionTimeframe"
            render={({ field: { ref: _, value, onChange, ...field } }) => {
                return (
                    <RadioButtonCard
                        value={value}
                        inputValue={inputValue}
                        description={description}
                        label={label}
                        onChange={() => onChange(inputValue)}
                        {...field}
                    />
                );
            }}
        />
    );
});

type CustomDispositionTimeInputProps = Partial<Pick<ControllerProps, 'defaultValue'>> &
    Pick<ControllerProps, 'name'> & {
        placeholder: string;
        initialTimeToFocus: string;
    };
const CustomDispositionTimeInput: React.FC<CustomDispositionTimeInputProps> = React.forwardRef<
    HTMLInputElement,
    CustomDispositionTimeInputProps
>(({ defaultValue, name, placeholder, initialTimeToFocus }, _ref) => {
    const { control } = useFormContext();

    return (
        <Controller
            control={control}
            name={name}
            defaultValue={defaultValue ?? null}
            render={({ field: { ref: _, value, onChange }, fieldState }) => {
                return (
                    <TimePicker
                        buttonClassName="h-[48px]"
                        optionsClassName="z-10 !max-h-[120px]"
                        placeholder={placeholder}
                        initialTimeToFocus={initialTimeToFocus}
                        value={value}
                        onChange={value => onChange(value ?? null)}
                        errorMessage={fieldState.error?.message}
                    />
                );
            }}
        />
    );
});
